// Adapted from the 21st.dev "Scroll Choreography" component.
// The original uses Tailwind; this project styles with plain CSS (scroll-choreography.css).
// Additions: a narrow-screen layout, reduced-motion support and an optional caption
// that fades in over the final full-screen image.
import React, {useEffect, useRef, useState} from "react";
import {motion, MotionValue, useReducedMotion, useScroll, useSpring, useTransform} from "framer-motion";
import {cn} from "../../lib/utils";
import "./scroll-choreography.css";

interface ScrollChoreographyProps {
    className?: string
    images: {
        topLeft: string
        topRight: string
        bottomLeft: string
        bottomRight: string
    }
    alts?: {
        topLeft?: string
        topRight?: string
        bottomLeft?: string
        bottomRight?: string
    }
    children?: React.ReactNode
}

const NARROW_QUERY = '(max-width: 699px)'

const useIsNarrow = () => {
    const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches)
    useEffect(() => {
        const query = window.matchMedia(NARROW_QUERY)
        const handleChange = () => setIsNarrow(query.matches)
        query.addEventListener('change', handleChange)
        return () => query.removeEventListener('change', handleChange)
    }, [])
    return isNarrow
}

export function ScrollChoreography({className, images, alts = {}, children}: ScrollChoreographyProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const prefersReducedMotion = useReducedMotion()
    const isNarrow = useIsNarrow()

    const {scrollYProgress} = useScroll({
        target: containerRef,
        offset: ['start start', 'end end'],
    })

    const springProgress = useSpring(scrollYProgress, {
        stiffness: 400,
        damping: 50,
        mass: 1.2,
        restDelta: 0.001,
    })
    const progress: MotionValue<number> = prefersReducedMotion ? scrollYProgress : springProgress

    // Default positions relative to center. Narrow screens use bigger tiles closer together.
    const xLeft = isNarrow ? '-24vw' : '-20vw'
    const xRight = isNarrow ? '24vw' : '20vw'
    const yTop = isNarrow ? '-13vh' : '-14vh'
    const yBottom = isNarrow ? '13vh' : '14vh'
    const tileWidth = isNarrow ? '44vw' : '36vw'
    const tileHeight = isNarrow ? '22vh' : '24vh'

    // Phase 1: 0 - 0.3 diagonal movement
    // Phase 2: 0.35 - 0.65 stack alignment to center
    // Phase 3: 0.7 - 0.9 top right expands to full screen
    const stops = [0, 0.3, 0.35, 0.65, 1]

    const tlX = useTransform(progress, stops, [xLeft, xLeft, xLeft, '0vw', '0vw'])
    const tlY = useTransform(progress, stops, [yTop, yBottom, yBottom, '0vh', '0vh'])

    const brX = useTransform(progress, stops, [xRight, xRight, xRight, '0vw', '0vw'])
    const brY = useTransform(progress, stops, [yBottom, yTop, yTop, '0vh', '0vh'])

    const blX = useTransform(progress, stops, [xLeft, xLeft, xLeft, '0vw', '0vw'])
    const blY = useTransform(progress, stops, [yBottom, yBottom, yBottom, '0vh', '0vh'])

    const trX = useTransform(progress, stops, [xRight, xRight, xRight, '0vw', '0vw'])
    const trY = useTransform(progress, stops, [yTop, yTop, yTop, '0vh', '0vh'])

    const heroWidth = useTransform(progress, [0.65, 0.7, 0.9, 1], [tileWidth, tileWidth, '100vw', '100vw'])
    const heroHeight = useTransform(progress, [0.65, 0.7, 0.9, 1], [tileHeight, tileHeight, '100vh', '100vh'])

    const underImagesOpacity = useTransform(progress, [0.75, 0.85], [1, 0])
    const captionOpacity = useTransform(progress, [0.88, 0.96], [0, 1])
    const captionY = useTransform(progress, [0.88, 0.96], [24, 0])

    const tileStyle = {width: tileWidth, height: tileHeight}

    return (
        <div ref={containerRef} className={cn('scroll-choreo', className)}>
            <div className={'scroll-choreo-sticky'}>
                <div className={'scroll-choreo-stage'}>
                    <motion.div style={{...tileStyle, x: tlX, y: tlY, opacity: underImagesOpacity}} className={'scroll-choreo-tile'} data-layer={1}>
                        <img src={images.topLeft} alt={alts.topLeft ?? ''}/>
                    </motion.div>
                    <motion.div style={{...tileStyle, x: brX, y: brY, opacity: underImagesOpacity}} className={'scroll-choreo-tile'} data-layer={2}>
                        <img src={images.bottomRight} alt={alts.bottomRight ?? ''}/>
                    </motion.div>
                    <motion.div style={{...tileStyle, x: blX, y: blY, opacity: underImagesOpacity}} className={'scroll-choreo-tile'} data-layer={3}>
                        <img src={images.bottomLeft} alt={alts.bottomLeft ?? ''}/>
                    </motion.div>
                    <motion.div style={{x: trX, y: trY, width: heroWidth, height: heroHeight}} className={'scroll-choreo-tile scroll-choreo-hero'} data-layer={4}>
                        <img src={images.topRight} alt={alts.topRight ?? ''}/>
                    </motion.div>
                    {children &&
                        <motion.div className={'scroll-choreo-caption'} style={{opacity: captionOpacity, y: captionY}}>
                            {children}
                        </motion.div>
                    }
                </div>
            </div>
        </div>
    )
}

export default ScrollChoreography
